import React, { FunctionComponent, useState } from 'react';
import { DuplicateIcon, TextInput } from 'evergreen-ui';
import styles from './RecruiteeCard.module.css';
import AppButton from 'components/AppButton/AppButton';
import DialogComponent from 'components/Dialog/Dialog';

interface RecruiteeKeyDialogProps {
    recruiteeKey?: string;
    onClose: () => void;
}

/**
 * Shows a freshly generated Recruitee key. It is open while a key is passed
 * in; the backend never returns the key again, so a backdrop click does not
 * close it.
 */
const RecruiteeKeyDialog: FunctionComponent<RecruiteeKeyDialogProps> = (
    props,
) => {
    const { recruiteeKey, onClose } = props;
    const [isKeyCopied, setIsKeyCopied] = useState(false);

    const handleCopyClick = () => {
        if (!recruiteeKey) {
            return;
        }
        navigator.clipboard.writeText(recruiteeKey).then(() => {
            setIsKeyCopied(true);
            setTimeout(() => {
                setIsKeyCopied(false);
            }, 2000);
        });
    };

    const handleClose = () => {
        setIsKeyCopied(false);
        onClose();
    };

    return (
        <DialogComponent
            title="Your Recruitee key"
            closeButtonText="Done"
            isShown={!!recruiteeKey}
            onClose={handleClose}
            disableBackdropClose>
            <div className={styles.dialog__content}>
                <p className={styles.key_dialog__warning}>
                    You won't be able to see this key again. Copy it now and
                    paste it into Recruitee.
                </p>
                <div className={styles.key_dialog__key}>
                    <TextInput
                        readOnly
                        aria-label="Recruitee key"
                        value={recruiteeKey ?? ''}
                        fontFamily="mono"
                        width="100%"
                        onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                            event.target.select()
                        }
                    />
                    <AppButton icon={DuplicateIcon} onClick={handleCopyClick}>
                        {isKeyCopied ? 'Copied!' : 'Copy'}
                    </AppButton>
                </div>
                <p className={styles.key_dialog__steps_title}>
                    Connect Recruitee
                </p>
                <ol className={styles.key_dialog__steps}>
                    <li>
                        In Tellent Recruitee, go to <b>Marketplace</b> and open{' '}
                        <b>Neurolytics</b>.
                    </li>
                    <li>
                        Paste this key into the <b>Token</b> field.
                    </li>
                    <li>
                        Click <b>Integrate</b>.
                    </li>
                </ol>
                <p>
                    Already connected? Open the Neurolytics integration in
                    Recruitee, click <b>Change</b> and paste the new key.
                </p>
            </div>
        </DialogComponent>
    );
};

export default RecruiteeKeyDialog;
